import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import i18n from 'simple-react-i18n'
import { Box, Button, Card, CardContent, Grid, Stack } from '@mui/material'
import { styled } from '@mui/material/styles'
import { compact } from 'lodash'
import AdministrationAction from '../../actions/AdministrationAction'
import { CardTitle } from '../../../components/card/NewCard'
import { CardTable } from '../../../components/datatable/NewTable'
import Checkbox from '../../../components/forms/Checkbox'
import Input from '../../../components/forms/Input'
import { StyledFieldSet, StyledLegend } from '../../../components/StyledElements'
import { nbPerPageLabel } from '../../../referencial/constants/ReferencialConstants'
import useActions from '../../../utils/customHook/useActions'
import { searchAllCharacters } from '../../../utils/StringUtil'
import qualityMarker from '../../../assets/pictures/markers/map_qualite.png'
import piezometryMarker from '../../../assets/pictures/markers/map_piezo.png'
import hydrometryMarker from '../../../assets/pictures/markers/map_hydro.png'
import pluviometryMarker from '../../../assets/pictures/markers/map_pluvio.png'

const Img = styled('img')({
    width: '20px',
    height: '25px',
    margin: '0px 5px',
})

const DESCRIPTION_WIDTH = 500

const MODEL_TYPES = [
    { key: 'stationSheet', label: () => i18n.stationSheet },
    { key: 'research', label: () => i18n.researchReports },
    { key: 'campaign', label: () => i18n.campaigns },
]

const STATION_TYPES = [
    { key: 'quality', marker: qualityMarker, tooltip: () => i18n.quality },
    { key: 'piezometry', marker: piezometryMarker, tooltip: () => i18n.piezometry },
    { key: 'hydrometry', marker: hydrometryMarker, tooltip: () => i18n.hydrometry },
    { key: 'pluviometry', marker: pluviometryMarker, tooltip: () => i18n.pluviometry },
]

const getKeywordTypeLabel = keywordType => ({
    cellule: i18n.cell,
    tableau: i18n.table,
    onglet: i18n.excelSheet,
})[keywordType] ?? keywordType

const DEFAULT_FILTER = {
    searchValue: '',
    stationSheet: true,
    research: true,
    campaign: true,
    quality: true,
    piezometry: true,
    hydrometry: true,
    pluviometry: true,
}

const isMatchingFilter = (keyword, filter) => {
    const search = searchAllCharacters(filter.searchValue)
    const isMatchingSearch = !search || searchAllCharacters(`${keyword.keyword} ${keyword.description || ''}`).includes(search)
    const isMatchingModelType = MODEL_TYPES.some(({ key }) => filter[key] && keyword[key])
    const isMatchingStationType = STATION_TYPES.some(({ key }) => filter[key] && keyword[key])
    return isMatchingSearch && isMatchingModelType && isMatchingStationType
}

const ModelKeywordsPanel = () => {
    const dispatch = useDispatch()

    const [keywords, setKeywords] = useState([])
    const [filter, setFilter] = useState(DEFAULT_FILTER)
    const [appliedFilter, setAppliedFilter] = useState(DEFAULT_FILTER)

    useActions(() => ({}), [])

    // construits au rendu : i18n n'est pas encore chargé à l'import du module
    const headers = [
        'keyword',
        'keywordType',
        { key: 'description', width: DESCRIPTION_WIDTH },
        'modelType',
        ...STATION_TYPES.map(({ key, marker, tooltip }) => ({ key, value: <Img src={marker} />, tooltip: tooltip() })),
    ]

    useEffect(() => {
        dispatch(AdministrationAction.fetchModelKeywords()).then(setKeywords)
    }, [dispatch])

    const onReset = () => {
        setFilter(DEFAULT_FILTER)
        setAppliedFilter(DEFAULT_FILTER)
    }

    const rows = useMemo(() => keywords
        .filter(keyword => isMatchingFilter(keyword, appliedFilter))
        .map(keyword => ({
            keyword: keyword.keyword,
            keywordType: getKeywordTypeLabel(keyword.keywordType),
            // le thème force les cellules sur une ligne : la description revient à la ligne dans sa largeur
            description: { value: <Box sx={{ whiteSpace: 'normal' }}>{keyword.description}</Box>, sortValue: keyword.description },
            modelType: compact(MODEL_TYPES.map(({ key, label }) => keyword[key] && label())).join(', '),
            ...STATION_TYPES.reduce((acc, { key }) => ({ ...acc, [key]: keyword[key] ? i18n.yes : i18n.no }), {}),
        })), [keywords, appliedFilter])

    return (
        <Stack spacing={1} direction='column'>
            <Card>
                <CardTitle title={i18n.criterias} color={'bannerColor'} />
                <CardContent>
                    <Grid container spacing={1} alignItems='center'>
                        <Grid size={6}>
                            <Input
                                title={i18n.search}
                                value={filter.searchValue}
                                onChange={value => setFilter(prev => ({ ...prev, searchValue: value }))}
                                onEnterKeyPress={() => setAppliedFilter(filter)}
                            />
                        </Grid>
                        <Grid size={6} />
                        <Grid size={6}>
                            <StyledFieldSet>
                                <StyledLegend>{i18n.modelTypes}</StyledLegend>
                                <Grid container spacing={1}>
                                    {MODEL_TYPES.map(({ key, label }) => (
                                        <Grid key={key} size={4}>
                                            <Checkbox
                                                label={label()}
                                                checked={filter[key]}
                                                onChange={value => setFilter(prev => ({ ...prev, [key]: value }))}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            </StyledFieldSet>
                        </Grid>
                        <Grid size={6}>
                            <StyledFieldSet>
                                <StyledLegend>{i18n.stationTypes}</StyledLegend>
                                <Grid container spacing={1}>
                                    {STATION_TYPES.map(({ key, marker, tooltip }) => (
                                        <Grid key={key} size={3}>
                                            <Checkbox
                                                label={<Img src={marker} />}
                                                tooltip={tooltip()}
                                                checked={filter[key]}
                                                onChange={value => setFilter(prev => ({ ...prev, [key]: value }))}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            </StyledFieldSet>
                        </Grid>
                        <Grid size={12} container spacing={1} justifyContent='flex-end'>
                            <Button variant='outlined' onClick={onReset}>{i18n.reinit}</Button>
                            <Button variant='contained' onClick={() => setAppliedFilter(filter)}>{i18n.search}</Button>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
            <CardTable
                color={'bannerColor'}
                title={i18n.keywords}
                rows={rows}
                headers={headers}
                rowsPerPageOptions={nbPerPageLabel}
            />
        </Stack>
    )
}

export default ModelKeywordsPanel
